/*
 * סינתזה — הצליל נוצר בזמן ריצה, ואין בפרויקט אף קובץ אודיו.
 *
 * זו לא קמצנות בבייטים. ה-CSP הוא `default-src 'self'`, המשחק חייב לעבוד אופליין
 * מלא, וחבילת WAV/OGG לעשרים צלילים הייתה מכפילה את גודל הבנייה. אוסצילטור, רעש
 * לבן, מעטפת ופילטר נותנים "פיצוץ" ו"קליק" משכנעים מספיק לסגנון של RA2 — במחיר
 * של אפס בקשות רשת.
 *
 * הקובץ הזה **אינו מכיר אף צליל בשמו.** הוא יודע לבנות שכבה מתוך תיאור דאטה
 * (`Layer`), ותו לא. הקטלוג עצמו יושב ב-sounds.ts, בדיוק כמו שהקרב יושב
 * ב-content/combat ולא ב-sim/combat.
 */

export type WaveKind = 'sine' | 'square' | 'sawtooth' | 'triangle';

/**
 * מעטפת ADSR עם שלב החזקה מפורש.
 *
 * `sustain` הוא **רמה** (0..1 מהשיא) ו-`hold` הוא **זמן** באותה רמה. בלי ההפרדה
 * הזו אי אפשר לתאר גם "קליק" (decay מהיר ל-0) וגם "צפירה" (החזקה ארוכה) באותו מבנה.
 */
export interface Envelope {
    /** שניות מ-0 לשיא. */
    readonly attack: number;
    /** שניות מהשיא לרמת ה-sustain. */
    readonly decay: number;
    /** רמה יחסית לשיא, 0..1. */
    readonly sustain: number;
    /** שניות החזקה ברמת ה-sustain. */
    readonly hold: number;
    /** שניות מרמת ה-sustain ל-0. */
    readonly release: number;
}

export interface FilterSpec {
    readonly type: 'lowpass' | 'highpass' | 'bandpass';
    /** הרץ בתחילת הצליל. */
    readonly frequency: number;
    /** הרץ בסוף הצליל — טאטוא. חסר = תדר קבוע. */
    readonly endFrequency?: number;
    readonly q: number;
}

interface LayerBase {
    /** עוצמת השיא של השכבה, 0..1. סכום השכבות אמור להישאר סביר. */
    readonly gain: number;
    readonly env: Envelope;
    /** דחייה בשניות מתחילת הצליל — כך בונים אקורד שבור או "הד" של פיצוץ. */
    readonly delay?: number;
    readonly filter?: FilterSpec;
}

export interface ToneLayer extends LayerBase {
    readonly source: 'tone';
    readonly wave: WaveKind;
    readonly frequency: number;
    /** תדר סיום — גליסנדו. פיצוץ הוא בעיקר תדר שנופל. */
    readonly endFrequency?: number;
    /** אפנון תדר: קצב הרעד בהרץ. */
    readonly vibratoHz?: number;
    /** עומק הרעד בהרץ. */
    readonly vibratoDepth?: number;
}

export interface NoiseLayer extends LayerBase {
    readonly source: 'noise';
}

export type Layer = ToneLayer | NoiseLayer;

/** אורך מאגר הרעש. ארוך מספיק כדי שלולאה לא תישמע כמו טון. */
const NOISE_SECONDS = 2;

/** שוליים אחרי סוף המעטפת, כדי ש-stop לא יקטע זנב שעדיין יורד. */
const TAIL_SECONDS = 0.02;

export function envelopeDuration(env: Envelope): number {
    return env.attack + env.decay + env.hold + env.release;
}

export function layerDuration(layer: Layer): number {
    return (layer.delay ?? 0) + envelopeDuration(layer.env);
}

export function layersDuration(layers: readonly Layer[]): number {
    let longest = 0;
    for (const layer of layers) longest = Math.max(longest, layerDuration(layer));
    return longest;
}

/*
 * מאגר הרעש נבנה פעם אחת להקשר. מילוי 96,000 דגימות בכל ירייה היה מייצר זבל
 * בקצב של עשרות מגהבייט בדקת קרב.
 */
const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();

export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
    const cached = noiseBuffers.get(ctx);
    if (cached) return cached;
    const rate = ctx.sampleRate > 0 ? ctx.sampleRate : 44100;
    const frames = Math.max(1, Math.floor(rate * NOISE_SECONDS));
    const buffer = ctx.createBuffer(1, frames, rate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseBuffers.set(ctx, buffer);
    return buffer;
}

export interface VoiceOptions {
    /** מתי להתחיל, בשעון של ה-AudioContext. */
    readonly startAt: number;
    /** מכפיל תדר — הווריאציה שמונעת משתי יריות להישמע זהות. */
    readonly pitch: number;
    /** מכפיל עוצמה. */
    readonly gain: number;
}

export interface Voice {
    /** כל מקור שהותנע. **כל אחד מהם חייב לקבל stop** — אחרת הדף נחנק. */
    readonly sources: readonly AudioScheduledSourceNode[];
    /** כל הצמתים בשרשרת, לניתוק בסיום. */
    readonly nodes: readonly AudioNode[];
    /** מתי הקול נגמר, בשעון ההקשר. */
    readonly endsAt: number;
}

/**
 * בונה קול שלם ומתניע אותו. כל מקור מתוזמן ל-`stop` מראש, כך שגם אם `onended`
 * לא יגיע לעולם — הצומת מפסיק לצרוך מעבד.
 */
export function buildVoice(
    ctx: BaseAudioContext,
    destination: AudioNode,
    layers: readonly Layer[],
    options: VoiceOptions,
): Voice {
    const sources: AudioScheduledSourceNode[] = [];
    const nodes: AudioNode[] = [];
    let endsAt = options.startAt;

    for (const layer of layers) {
        const built = buildLayer(ctx, destination, layer, options);
        sources.push(...built.sources);
        nodes.push(...built.nodes);
        endsAt = Math.max(endsAt, built.endsAt);
    }

    return { sources, nodes, endsAt };
}

interface BuiltLayer {
    readonly sources: readonly AudioScheduledSourceNode[];
    readonly nodes: readonly AudioNode[];
    readonly endsAt: number;
}

function buildLayer(
    ctx: BaseAudioContext,
    destination: AudioNode,
    layer: Layer,
    options: VoiceOptions,
): BuiltLayer {
    const start = options.startAt + (layer.delay ?? 0);
    const endsAt = start + envelopeDuration(layer.env);
    const stopAt = endsAt + TAIL_SECONDS;

    const sources: AudioScheduledSourceNode[] = [];
    const nodes: AudioNode[] = [];

    const amp = ctx.createGain();
    nodes.push(amp);
    applyEnvelope(amp.gain, layer.env, clamp01(layer.gain * options.gain), start);
    amp.connect(destination);

    let head: AudioNode = amp;
    if (layer.filter) {
        const filter = ctx.createBiquadFilter();
        nodes.push(filter);
        filter.type = layer.filter.type;
        rampFrequency(
            filter.frequency,
            layer.filter.frequency * options.pitch,
            layer.filter.endFrequency === undefined
                ? undefined
                : layer.filter.endFrequency * options.pitch,
            start,
            endsAt,
        );
        filter.Q.value = layer.filter.q;
        filter.connect(amp);
        head = filter;
    }

    if (layer.source === 'tone') {
        const osc = ctx.createOscillator();
        osc.type = layer.wave;
        rampFrequency(
            osc.frequency,
            layer.frequency * options.pitch,
            layer.endFrequency === undefined ? undefined : layer.endFrequency * options.pitch,
            start,
            endsAt,
        );
        osc.connect(head);
        osc.start(start);
        osc.stop(stopAt);
        sources.push(osc);
        nodes.push(osc);

        if (layer.vibratoHz !== undefined && layer.vibratoDepth !== undefined) {
            const lfo = ctx.createOscillator();
            lfo.type = 'sine';
            lfo.frequency.value = layer.vibratoHz;
            const depth = ctx.createGain();
            depth.gain.value = layer.vibratoDepth;
            lfo.connect(depth);
            depth.connect(osc.frequency);
            lfo.start(start);
            lfo.stop(stopAt);
            sources.push(lfo);
            nodes.push(lfo, depth);
        }
    } else {
        const noise = ctx.createBufferSource();
        noise.buffer = noiseBuffer(ctx);
        noise.loop = true;
        noise.playbackRate.value = options.pitch;
        noise.connect(head);
        noise.start(start);
        noise.stop(stopAt);
        sources.push(noise);
        nodes.push(noise);
    }

    return { sources, nodes, endsAt: stopAt };
}

function applyEnvelope(param: AudioParam, env: Envelope, peak: number, start: number): void {
    const afterAttack = start + env.attack;
    const afterDecay = afterAttack + env.decay;
    const afterHold = afterDecay + env.hold;
    const afterRelease = afterHold + env.release;
    const held = peak * env.sustain;

    param.setValueAtTime(0, start);
    param.linearRampToValueAtTime(peak, afterAttack);
    param.linearRampToValueAtTime(held, afterDecay);
    param.setValueAtTime(held, afterHold);
    param.linearRampToValueAtTime(0, afterRelease);
}

/*
 * טאטוא תדר ב-exponential ולא ב-linear: האוזן שומעת גובה לוגריתמית, ופגז שנופל
 * מ-140 ל-45 בקו ישר נשמע כמו קפיצה ואז שקט. שים לב ש-exponential מתפוצץ על 0,
 * ולכן יש רצפה.
 */
function rampFrequency(
    param: AudioParam,
    from: number,
    to: number | undefined,
    start: number,
    end: number,
): void {
    const head = Math.max(1, from);
    param.setValueAtTime(head, start);
    if (to === undefined) return;
    const tail = Math.max(1, to);
    if (end > start) param.exponentialRampToValueAtTime(tail, end);
    else param.setValueAtTime(tail, start);
}

function clamp01(value: number): number {
    if (!Number.isFinite(value)) return 0;
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
}
